import { useRouter } from '@abyss/web/hooks/useRouter';
import { Button } from '@abyss/web/ui/Button';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { t } from 'i18next';
import { useShallow } from 'zustand/react/shallow';

import { useCoverageType } from '../../hooks/useCoverageType';
import { useLagoon } from '../../hooks/useLagoon';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { CardHeader } from '../CardHeader/CardHeader';
import { CommonlySearchedCard } from '../CommonlySearched';
import { Constants, ReverseCoverageTypesCodes } from '../Constants';
import { cardHeaderNullPageStyle } from '../ConstantsStyles';
import {
  ButtonStyles,
  CommonlySearchedStyled,
  Content,
  IconStyles,
  NullPageContainerStyled,
  NullResultSectionStyled,
  SubHeader,
  Wrapper,
} from './CustomNullResultsPage.styles';

export type CustomNullSearchPageProps = {
  searchTitle: string;
  specialtyCode: string;
  emptyResults: boolean;
  specialtySearch?: boolean;
  onCommonSearchChipClicked: () => void;
  active?: boolean;
};

export const CustomNullSearchPage = ({ onCommonSearchChipClicked }) => {
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const { navigate } = useRouter();
  const coverageType = useCoverageType();
  const lagoonData = useLagoon('null-search-page-common')();

  const filteredLagoonData = lagoonData?.find(
    ({ pageType }) => pageType === 'CUSTOM_NSR'
  );

  const { ALL, BEHAVIORAL_HEALTH, VISION, DENTAL } = Constants.CHIPS_CATEGORIES;
  const getSelectedChipValue = () => {
    if (coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'])
      return BEHAVIORAL_HEALTH;
    if (coverageType === ReverseCoverageTypesCodes.VISION) return VISION;
    if (coverageType === ReverseCoverageTypesCodes.DENTAL) return DENTAL;

    return ALL;
  };

  const handleBack = () => {
    setTypeaheadSearchStore({
      searchType: Constants.SEARCH_TYPES.NAME,
      previous: true,
    });
    navigate(-1);
  };

  return (
    <NullPageContainerStyled>
      <NullResultSectionStyled>
        <Wrapper
          data-auto-testid="custom-null-page"
          data-testid="custom-null-page"
        >
          <CardHeader
            css={cardHeaderNullPageStyle}
            data-auto-testid="custom-browse-common-specialities-heading"
            data-testid="custom-browse-common-specialities-heading"
            offset={3}
          >
            {filteredLagoonData?.heading}
          </CardHeader>
          <SubHeader
            color="$gray7"
            css={{
              '&.abyss-heading-root': {
                fontWeight: '$semibold',
                fontSize: '14.22px',
                lineHeight: '16px',
              },
            }}
            data-auto-testid="custom-browse-common-specialities-sub-heading"
            data-testid="custom-browse-common-specialities-sub-heading"
            offset={5}
          >
            {filteredLagoonData.subHeading}
          </SubHeader>
          <Content>{filteredLagoonData.content}</Content>
          <Button
            css={ButtonStyles}
            data-auto-testid="custom-back-to-previous-page"
            data-testid="custom-back-to-previous-page"
            onClick={handleBack}
            size="$md"
          >
            {t('NULL_PAGE.NULL_RESULTS_BACK_TO_PREVIOUS_PAGE_BUTTON')}
            <IconMaterial
              color="$white"
              css={IconStyles}
              icon="chevron_right"
              isScreenReadable
              size="24px"
            />
          </Button>
        </Wrapper>
      </NullResultSectionStyled>
      <CommonlySearchedStyled>
        <CommonlySearchedCard
          onCommonSearchChipClicked={onCommonSearchChipClicked}
          selectedChipValue={getSelectedChipValue()}
        />
      </CommonlySearchedStyled>
    </NullPageContainerStyled>
  );
};
